import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectWithoutProperties from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["clickOnEnter", "clickOnSpace"],
    _excluded2 = ["view"],
    _excluded3 = ["view"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useRef, useState, useEffect } from 'react';
import { isButton, isTextField } from 'ariakit-utils/dom';
import { isSelfTarget, queueBeforeEvent, fireClickEvent } from 'ariakit-utils/events';
import { useTagName, useEvent, useForkRef } from 'ariakit-utils/hooks';
import { createHook, createComponent, createElement } from 'ariakit-utils/system';
import { useFocusable } from '../focusable/focusable.js';

function isNativeClick(event) {
  if (!event.isTrusted) return false; // istanbul ignore next: can't test trusted events yet

  var element = event.currentTarget;
  return isButton(element) || element.tagName === "SUMMARY" || element.tagName === "INPUT" || element.tagName === "TEXTAREA" || element.tagName === "A" || element.tagName === "SELECT";
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component. If the element is not a native clickable element (like a
 * button), the hook will return additional props to make sure it's accessible.
 * @see https://ariakit.org/components/command
 * @example
 * ```jsx
 * const props = useCommand({ as: "div" });
 * <Role {...props}>Accessible button</Role>
 * ```
 */


var useCommand = createHook(function (_ref) {
  var _ref$clickOnEnter = _ref.clickOnEnter,
      clickOnEnter = _ref$clickOnEnter === void 0 ? true : _ref$clickOnEnter,
      _ref$clickOnSpace = _ref.clickOnSpace,
      clickOnSpace = _ref$clickOnSpace === void 0 ? true : _ref$clickOnSpace,
      props = _objectWithoutProperties(_ref, _excluded);

  var ref = useRef(null);
  var tagName = useTagName(ref, props.as);

  var _useState = useState(function () {
    return !!tagName && isButton({
      tagName: tagName,
      type: props.type
    });
  }),
      _useState2 = _slicedToArray(_useState, 2),
      isNativeButton = _useState2[0],
      setIsNativeButton = _useState2[1];

  useEffect(function () {
    if (!ref.current) return;
    setIsNativeButton(isButton(ref.current));
  }, []);

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      active = _useState4[0],
      setActive = _useState4[1];

  var activeRef = useRef(false);
  var isDuplicate = ("data-command" in props);
  var onKeyDownProp = props.onKeyDown;
  var onKeyDown = useEvent(function (event) {
    onKeyDownProp == null ? void 0 : onKeyDownProp(event);
    var element = event.currentTarget;
    if (event.defaultPrevented) return;
    if (isDuplicate) return;
    if (props.disabled) return;
    if (!isSelfTarget(event)) return;
    if (isTextField(element)) return;
    if (element.isContentEditable) return;
    var isEnter = clickOnEnter && event.key === "Enter";
    var isSpace = clickOnSpace && event.key === " ";
    var shouldPreventEnter = event.key === "Enter" && !clickOnEnter;
    var shouldPreventSpace = event.key === " " && !clickOnSpace;

    if (shouldPreventEnter || shouldPreventSpace) {
      event.preventDefault();
      return;
    }

    if (isEnter || isSpace) {
      var nativeClick = isNativeClick(event);

      if (isEnter) {
        if (!nativeClick) {
          event.preventDefault();

          var view = event.view,
              eventInit = _objectWithoutProperties(event, _excluded2);

          queueBeforeEvent(element, "keyup", function () {
            return (// Fire a click event instead of calling element.click() directly
              // so we can pass the modifier state to the click event.
              fireClickEvent(element, eventInit)
            );
          });
        }
      } else if (isSpace) {
        activeRef.current = true;

        if (!nativeClick) {
          event.preventDefault();
          setActive(true);
        }
      }
    }
  });
  var onKeyUpProp = props.onKeyUp;
  var onKeyUp = useEvent(function (event) {
    onKeyUpProp == null ? void 0 : onKeyUpProp(event);
    if (event.defaultPrevented) return;
    if (isDuplicate) return;
    if (props.disabled) return;
    if (event.metaKey) return;
    var isSpace = clickOnSpace && event.key === " ";

    if (activeRef.current && isSpace) {
      activeRef.current = false;

      if (!isNativeClick(event)) {
        setActive(false);
        var element = event.currentTarget;

        var view = event.view,
            eventInit = _objectWithoutProperties(event, _excluded3);

        requestAnimationFrame(function () {
          return fireClickEvent(element, eventInit);
        });
      }
    }
  });
  props = _objectSpread(_objectSpread({
    "data-command": "",
    "data-active": active ? "" : undefined,
    type: isNativeButton ? "button" : undefined
  }, props), {}, {
    ref: useForkRef(ref, props.ref),
    onKeyDown: onKeyDown,
    onKeyUp: onKeyUp
  });
  props = useFocusable(props);
  return props;
});
/**
 * A component that renders a native clickable element (a button). If another
 * element is passed to the `as` prop, this component will make sure the
 * rendered element is accessible.
 * @see https://ariakit.org/components/command
 * @example
 * ```jsx
 * <Command as="div">Accessible button</Command>
 * ```
 */

var Command = createComponent(function (props) {
  props = useCommand(props);
  return createElement("button", props);
});
export { Command, useCommand };